export const unwrapValue = (input: unknown): unknown => {
    let value = input;
    while (
        value !== null &&
        value !== undefined &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.prototype.hasOwnProperty.call(value, 'value')
    ) {
        value = (value as {value: unknown}).value;
    }
    return value;
};

export const parseNumber = (input: unknown): number | null => {
    const value = unwrapValue(input);
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const normalized = value.replace(/\s/g, '').replace(',', '.');
        const parsed = Number.parseFloat(normalized);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return null;
};

export const parseString = (input: unknown, fallback = ''): string => {
    const value = unwrapValue(input);
    if (value === null || value === undefined) {
        return fallback;
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
    }
    return fallback;
};

export const parseNullableString = (input: unknown): string | null => {
    const value = parseString(input, '');
    return value ? value : null;
};

export const parseEnum = <T extends string>(input: unknown, values: readonly T[], fallback: T): T => {
    const value = parseString(input, fallback);
    return values.includes(value as T) ? value as T : fallback;
};

export const toDisplayText = (input: unknown, fallback = '-'): string => {
    const value = unwrapValue(input);
    if (value === null || value === undefined || value === '') {
        return fallback;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

export const toDisplayNumber = (input: unknown): number | null => parseNumber(input);

export const parseJsonLike = (input: unknown): unknown | null => {
    const value = unwrapValue(input);
    if (!value) {
        return null;
    }
    if (typeof value === 'object') {
        return value;
    }
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }
    if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
        try {
            return JSON.parse(trimmed);
        } catch {
            return null;
        }
    }
    return null;
};
